import React, { useMemo, useState } from 'react';
import { AlertTriangle, ArrowRight, CalendarDays, CheckCircle2, ChevronDown, ChevronUp, Clock, Coins, Info, Landmark, ShieldAlert, XCircle } from 'lucide-react';
import { Badge, Card } from './AdminPanelPrimitives';
import { formatEuropeanDateTime } from '../../utils/dateFormat';
import { TierMixCard } from './AdminAnalytics/TierMixCard';

// =============================================================================
// FINANCIAL PROTOCOL — READ BEFORE EDITING THIS FILE
// -----------------------------------------------------------------------------
// This file renders the admin Money Flow breakdown. Any change to a fee,
// percentage, commission rate, or flat-fee constant MUST be accompanied by a
// synchronized update to BOTH:
//   - /app/memory/FINANCIAL_PROTOCOL.md         (source-of-truth doc)
//   - /app/backend/tests/test_financial_protocol.py  (regression test suite)
// Never paraphrase rates from memory — always source them from constants.
// IMPORTANT: the future-tier rate (3%) is admin-visible only. Host-facing UI
// must hide the exact % and show "?% coming soon" to keep motivation high.
// When in doubt, ASK THE USER. Do not guess. (See FINANCIAL_PROTOCOL.md §4.)
// =============================================================================

const formatMoney = (value) => `${Number(value || 0).toFixed(2)}€`;

// === Canonical money-flow constants (see /app/memory/FINANCIAL_PROTOCOL.md) ===
// Source of truth lives in /app/backend/server.py + /app/backend/utils.py.
// Mirrored here so the admin can model arbitrary bills locally. Update both
// the constants AND the protocol doc in lockstep — never edit one in isolation.
const MONEY_FLOW = {
  GUEST_DISCOUNT_PERCENT: 10.0,
  GUEST_FLAT_FEE_EUR: 0.39,
  VENUE_FLAT_FEE_EUR: 0.39,
  VENUE_FLAT_TO_HOST_EUR: 0.20,
  VENUE_FLAT_TO_ALLURE_EUR: 0.19,
  VIVA_VARIABLE: 0.017, // 1.7% of TOTAL CHARGED (discounted + guest flat)
  VIVA_FIXED_EUR: 0.10,
  ALLURE_APP_VARIABLE: 0.031, // 3.1% of discounted bill
  POOL_STANDARD_RATE: 0.001, // 0.1%
  POOL_GAP_RATE: 0.01, // 1.0% — base tier only
  IBAN_SEPA_FEE_EUR: 0.80,
};

const HOST_TIERS = [
  { id: 'base',     label: 'Base 0.5%',           rate: 0.5, status: 'Active',       comingSoon: false },
  { id: 'unlocked', label: 'Unlocked 2%',         rate: 2.0, status: 'Via referrals',comingSoon: false },
  { id: 'future',   label: '3%',                  rate: 3.0, status: 'Coming soon',  comingSoon: true  },
];

const round2 = (n) => Math.round(n * 100) / 100;

const computeMoneyFlow = (venueOriginalPrice, hostRate) => {
  const original = Math.max(0, Number(venueOriginalPrice) || 0);
  const discount = round2(original * (MONEY_FLOW.GUEST_DISCOUNT_PERCENT / 100));
  const discounted = round2(original - discount);
  const guestFlatFee = MONEY_FLOW.GUEST_FLAT_FEE_EUR;
  const guestPaysTotal = round2(discounted + guestFlatFee);
  // Viva charges on the actual amount processed at the card terminal.
  const vivaFee = round2(guestPaysTotal * MONEY_FLOW.VIVA_VARIABLE + MONEY_FLOW.VIVA_FIXED_EUR);
  // App gross = 3.1% × discounted + 0.39 venue flat + 0.39 guest flat.
  const appFee = round2(
    discounted * MONEY_FLOW.ALLURE_APP_VARIABLE
    + MONEY_FLOW.VENUE_FLAT_FEE_EUR
    + MONEY_FLOW.GUEST_FLAT_FEE_EUR
  );
  // Host = rate% × discounted + 0.20 (venue-flat slice).
  const hostCommission = round2(discounted * (hostRate / 100) + MONEY_FLOW.VENUE_FLAT_TO_HOST_EUR);
  // Pool = 0.1% × discounted + (gap 1.0% if base tier else 0) + 0.39 guest flat.
  const poolStandard = round2(discounted * MONEY_FLOW.POOL_STANDARD_RATE);
  const poolGap = hostRate < 2.0 ? round2(discounted * MONEY_FLOW.POOL_GAP_RATE) : 0;
  const poolGuestFlat = MONEY_FLOW.GUEST_FLAT_FEE_EUR;
  const pool = round2(poolStandard + poolGap + poolGuestFlat);
  // Venue payout: total processed − Viva − app gross = venue receives.
  const venueProfit = round2(guestPaysTotal - vivaFee - appFee);
  // Allure net = app gross − host − pool. Conservation must hold.
  const allureNet = round2(appFee - hostCommission - pool);
  return {
    original, discount, discounted, guestFlatFee, guestPaysTotal,
    vivaFee, appFee, hostCommission, pool, venueProfit, allureNet,
    poolStandard, poolGap, poolGuestFlat,
  };
};

const TONE_COLORS = {
  guest: { bg: '#FFF7EE', border: '#F3D9B0', text: '#7A4C0E' },
  viva: { bg: '#F2F4FF', border: '#C9D2FF', text: '#1F2A78' },
  allure: { bg: '#F3EEFF', border: '#D9C9FF', text: '#3F1F8C' },
  host: { bg: '#EAF7EE', border: '#BCE6C8', text: '#0F6B19' },
  venue: { bg: '#EAF4FB', border: '#BBD8EC', text: '#0E4D7D' },
};

const FlowRow = ({ rowKey, role, formula, amount, tone, hint }) => {
  const palette = TONE_COLORS[tone] || TONE_COLORS.allure;
  return (
    <div
      data-testid={`admin-finance-money-flow-row-${rowKey}`}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 14,
        border: `1px solid ${palette.border}`,
        background: palette.bg,
      }}
    >
      <div style={{ display: 'grid', gap: 2, minWidth: 220, flex: 1 }}>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: palette.text }}>{role}</p>
        <p style={{ margin: 0, fontSize: 12, color: '#555' }}>{formula}</p>
        {hint ? <p style={{ margin: 0, fontSize: 11, color: '#888', fontStyle: 'italic' }}>{hint}</p> : null}
      </div>
      <p
        data-testid={`admin-finance-money-flow-amount-${rowKey}`}
        style={{ margin: 0, fontSize: 18, fontWeight: 700, color: palette.text }}
      >
        {amount}
      </p>
    </div>
  );
};

const MoneyFlowBreakdownCard = () => {
  const [open, setOpen] = useState(false);
  const [billInput, setBillInput] = useState('100');
  const [tierId, setTierId] = useState('base');

  const venuePrice = Math.max(0, Number(billInput) || 0);
  const activeTier = HOST_TIERS.find((tier) => tier.id === tierId) || HOST_TIERS[0];
  const flow = useMemo(() => computeMoneyFlow(venuePrice, activeTier.rate), [venuePrice, activeTier.rate]);

  return (
    <Card data-testid="admin-finance-money-flow-breakdown" style={{ display: 'grid', gap: open ? 14 : 0 }}>
      <button
        type="button"
        data-testid="admin-finance-money-flow-toggle"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        style={{
          all: 'unset',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: 4,
        }}
      >
        <div style={{ display: 'grid', gap: 2 }}>
          <p style={{ margin: 0, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Where the money goes</p>
          <p style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
            Example on a {formatMoney(venuePrice || 100)} bill
          </p>
          {!open ? (
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#888' }}>
              Tap to see the exact split — guest, Viva, Allure, host & venue
            </p>
          ) : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#3F1F8C', fontWeight: 700, fontSize: 13 }}>
          <span>{open ? 'Hide' : 'Show'}</span>
          {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>

      {open ? (
        <>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              padding: '10px 12px',
              borderRadius: 14,
              border: '1px solid #EFEFEF',
              background: '#FAFAFA',
              alignItems: 'center',
            }}
          >
            <label htmlFor="admin-finance-money-flow-input" style={{ display: 'grid', gap: 4, minWidth: 200, flex: 1 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Venue{"'"}s original price (before guest discount)
              </span>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  id="admin-finance-money-flow-input"
                  data-testid="admin-finance-money-flow-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={billInput}
                  onChange={(event) => setBillInput(event.target.value)}
                  style={{
                    width: '100%',
                    borderRadius: 12,
                    border: '1px solid #E0E0E0',
                    padding: '10px 28px 10px 12px',
                    fontSize: 16,
                    fontWeight: 700,
                  }}
                />
                <span style={{ position: 'absolute', right: 12, fontWeight: 700, color: '#666' }}>€</span>
              </div>
            </label>

            <div style={{ display: 'grid', gap: 4, minWidth: 220, flex: 1 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Host commission tier
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {HOST_TIERS.map((tier) => {
                  const selected = tierId === tier.id;
                  return (
                    <button
                      key={tier.id}
                      type="button"
                      data-testid={`admin-finance-money-flow-tier-${tier.id}`}
                      onClick={() => setTierId(tier.id)}
                      style={{
                        border: selected ? '2px solid #0F6B19' : '1px solid #E0E0E0',
                        background: selected ? '#EAF7EE' : '#FFF',
                        borderRadius: 12,
                        padding: '8px 12px',
                        cursor: 'pointer',
                        display: 'grid',
                        gap: 2,
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{tier.label}</span>
                      <span style={{ fontSize: 11, color: tier.comingSoon ? '#A36500' : '#666', fontWeight: tier.comingSoon ? 700 : 400 }}>
                        {tier.status}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            <FlowRow
              rowKey="guest-pays"
              role="Guest pays at checkout"
              formula={`${formatMoney(flow.original)} − ${formatMoney(flow.discount)} (10% guest discount) + ${formatMoney(flow.guestFlatFee)} flat fee`}
              amount={formatMoney(flow.guestPaysTotal)}
              tone="guest"
              hint={`Discounted bill: ${formatMoney(flow.discounted)}`}
            />
            <FlowRow
              rowKey="viva"
              role="Viva Wallet (payment processor)"
              formula={`1.7% × ${formatMoney(flow.guestPaysTotal)} (total processed) + 0.10€`}
              amount={formatMoney(flow.vivaFee)}
              tone="viva"
              hint="Viva sees the card-terminal amount, not the discounted bill"
            />
            <FlowRow
              rowKey="venue"
              role="Venue receives (Viva payout)"
              formula={`${formatMoney(flow.guestPaysTotal)} − ${formatMoney(flow.vivaFee)} Viva − ${formatMoney(flow.appFee)} App gross`}
              amount={formatMoney(flow.venueProfit)}
              tone="venue"
              hint="IBAN-payout venues: subtract an extra 0.80€ SEPA wire fee"
            />
            <FlowRow
              rowKey="app-gross"
              role="App gross fee (before redistribution)"
              formula="3.1% × discounted + 0.39€ venue flat + 0.39€ guest flat"
              amount={formatMoney(flow.appFee)}
              tone="allure"
              hint="Splits into host + pool + Allure net below"
            />
            <FlowRow
              rowKey="host"
              role={`Host direct commission (${activeTier.label}${activeTier.comingSoon ? ' · coming soon' : ''})`}
              formula={`${activeTier.rate}% × discounted + 0.20€ venue-flat slice`}
              amount={formatMoney(flow.hostCommission)}
              tone="host"
              hint={activeTier.comingSoon ? 'Future tier — not yet active in payouts' : undefined}
            />
            <FlowRow
              rowKey="pool"
              role="Monthly host pool contribution"
              formula={
                flow.poolGap > 0
                  ? `0.1% × discounted (${formatMoney(flow.poolStandard)}) + 1.0% gap redistribution (${formatMoney(flow.poolGap)}) + 0.39€ guest flat`
                  : `0.1% × discounted (${formatMoney(flow.poolStandard)}) + 0.39€ guest flat`
              }
              amount={formatMoney(flow.pool)}
              tone="host"
              hint={
                flow.poolGap > 0
                  ? 'Base tier: the 1.5% commission gap routes 1.0% → pool, 0.5% → Allure'
                  : 'Unlocked / future tier: no gap redistribution'
              }
            />
            <FlowRow
              rowKey="allure"
              role="Allure platform (net keep)"
              formula={
                flow.poolGap > 0
                  ? '(1.0% base + 0.5% gap bonus) × discounted + 0.19€ venue-flat slice'
                  : `${Math.max(0, 3.1 - activeTier.rate - 0.1).toFixed(1)}% × discounted + 0.19€ venue-flat slice`
              }
              amount={formatMoney(flow.allureNet)}
              tone="allure"
              hint="Conservation: Viva + Venue + Host + Pool + Allure = Guest paid"
            />
          </div>

          <div
            data-testid="admin-finance-money-flow-conservation"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              borderRadius: 10,
              background: '#F5F5F5',
              border: '1px dashed #DDD',
            }}
          >
            <span style={{ fontSize: 12, color: '#666' }}>
              Sum check: Viva + Venue + Host + Pool + Allure ={' '}
              <strong>{formatMoney(flow.vivaFee + flow.venueProfit + flow.hostCommission + flow.pool + flow.allureNet)}</strong>{' '}
              · Guest paid <strong>{formatMoney(flow.guestPaysTotal)}</strong>
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              padding: '10px 12px',
              borderRadius: 12,
              background: '#FFF8E1',
              border: '1px solid #F4E4A8',
            }}
          >
            <Info size={16} color="#7A5A00" style={{ marginTop: 2, flexShrink: 0 }} />
            <p style={{ margin: 0, fontSize: 12, color: '#7A5A00', lineHeight: 1.5 }}>
              <strong>IBAN-payout venues</strong> absorb an extra <strong>0.80€ SEPA fee</strong> per transaction (deducted from their profit). Viva-paid venues pay nothing extra.
              {' '}<strong>Tips</strong> are 100% passed through to the host. Host commission tiers: <strong>0.5%</strong> base → <strong>2%</strong> unlocked via referrals → <strong>?% coming soon</strong>.
              {' '}<strong>Gap redistribution</strong>: when a host is on base 0.5%, the 1.5% unlock-gap routes 1.0% → pool, 0.5% → Allure.
              {' '}Canonical source: <code>/app/memory/FINANCIAL_PROTOCOL.md</code>.
            </p>
          </div>
        </>
      ) : null}
    </Card>
  );
};

const summaryCardStyle = {
  display: 'grid',
  gap: 6,
  minWidth: 180,
  flex: 1,
};

const BlockReasonBadge = ({ reason }) => {
  const labels = {
    missing_vat: 'Missing VAT',
    missing_iban: 'No IBAN',
    late_joiner: 'Late joiner',
    below_minimum: 'Below min — rolls over',
  };
  const colors = {
    missing_vat: 'red',
    missing_iban: 'orange',
    late_joiner: 'blue',
    below_minimum: 'purple',
  };
  return <Badge text={labels[reason] || reason} color={colors[reason] || 'orange'} />;
};

const PayoutScheduleCard = ({ financeOverview, onRunSchedulerNow, onRunConnectReminders }) => {
  const nextPayoutDate = financeOverview?.next_payout_date;
  const cutoffDay = financeOverview?.late_joiner_cutoff_day || 25;
  const blockedCount = financeOverview?.blocked_count || 0;
  const readyCount = financeOverview?.ready_count || 0;
  const minimumPayout = financeOverview?.minimum_payout_eur || 8;

  const formattedNextPayout = nextPayoutDate
    ? new Date(nextPayoutDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : 'N/A';

  return (
    <Card data-testid="admin-finance-payout-schedule" style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Clock size={18} color="#3F1F8C" />
        <p style={{ margin: 0, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Payout schedule</p>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
        <div style={{ flex: 1, minWidth: 160 }}>
          <p style={{ margin: 0, fontSize: 13, color: '#666' }}>Next automatic payout</p>
          <p data-testid="admin-finance-next-payout-date" style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700 }}>{formattedNextPayout}</p>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#888' }}>
            Runs at 03:00 UTC on the 1st of each month
          </p>
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <p style={{ margin: 0, fontSize: 13, color: '#666' }}>Late-joiner cutoff</p>
          <p style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700 }}>Day {cutoffDay}+</p>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#888' }}>
            Hosts activating on the {cutoffDay}th or later skip the next payout
          </p>
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <p style={{ margin: 0, fontSize: 13, color: '#666' }}>Minimum payout</p>
          <p data-testid="admin-finance-minimum-payout" style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700 }}>{formatMoney(minimumPayout)}</p>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#888' }}>
            Balances below this roll over to the next month
          </p>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, borderTop: '1px solid #F1F1F1', paddingTop: 12 }}>
        <div style={{
          flex: 1, minWidth: 120, padding: '10px 12px', borderRadius: 12,
          background: readyCount > 0 ? '#EAF7EE' : '#F5F5F5',
          border: readyCount > 0 ? '1px solid #BCE6C8' : '1px solid #EFEFEF',
        }}>
          <p style={{ margin: 0, fontSize: 12, color: '#666', fontWeight: 700 }}>READY TO PAY</p>
          <p data-testid="admin-finance-ready-count" style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 700, color: readyCount > 0 ? '#0F6B19' : '#999' }}>{readyCount}</p>
        </div>
        <div style={{
          flex: 1, minWidth: 120, padding: '10px 12px', borderRadius: 12,
          background: blockedCount > 0 ? '#FFF7EE' : '#F5F5F5',
          border: blockedCount > 0 ? '1px solid #F3D9B0' : '1px solid #EFEFEF',
        }}>
          <p style={{ margin: 0, fontSize: 12, color: '#666', fontWeight: 700 }}>BLOCKED</p>
          <p data-testid="admin-finance-blocked-count" style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 700, color: blockedCount > 0 ? '#B45309' : '#999' }}>{blockedCount}</p>
        </div>
      </div>
      <div style={{ display: 'grid', gap: 8, borderTop: '1px solid #F1F1F1', paddingTop: 12 }}>
        <p style={{ margin: 0, fontSize: 12, color: '#888', lineHeight: 1.5 }}>
          On the <strong>28th at 10:00 UTC</strong>, hosts due to be paid on the 1st but missing VAT or IBAN get an automatic
          {'\u00A0'}“connect now” reminder. Hosts who connect later are paid out <strong>instantly</strong> on connect.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button
            type="button"
            data-testid="admin-finance-run-scheduler-now-button"
            onClick={onRunSchedulerNow}
            style={{ border: 'none', borderRadius: 12, padding: '10px 14px', background: '#3F1F8C', color: '#FFF', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
          >
            Run monthly payout now
          </button>
          <button
            type="button"
            data-testid="admin-finance-run-connect-reminders-button"
            onClick={onRunConnectReminders}
            style={{ border: '1px solid #3F1F8C', borderRadius: 12, padding: '10px 14px', background: '#FFF', color: '#3F1F8C', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
          >
            Send connect reminders now
          </button>
        </div>
      </div>
    </Card>
  );
};

const SchedulerRunsCard = ({ runs }) => {
  const [expanded, setExpanded] = useState(false);
  if (!runs || runs.length === 0) return null;
  const displayRuns = expanded ? runs : runs.slice(0, 3);
  const jobLabel = (run) => (run.job_type === 'connect_reminder' ? 'Connect reminders' : 'Monthly payout');
  const runSummaryText = (run) => {
    const s = run.summary || {};
    if (run.job_type === 'connect_reminder') {
      return `${s.notified || 0} notified · ${s.checked || 0} checked`;
    }
    if (run.summary) {
      return `${s.executed || 0} paid · ${s.skipped || 0} skipped · ${s.failed || 0} failed`;
    }
    return '';
  };
  return (
    <Card data-testid="admin-finance-scheduler-runs" style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={{ margin: 0, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Scheduler run history</p>
        {runs.length > 3 && (
          <button type="button" onClick={() => setExpanded((v) => !v)} style={{ all: 'unset', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#3F1F8C' }}>
            {expanded ? 'Show less' : `Show all (${runs.length})`}
          </button>
        )}
      </div>
      {displayRuns.map((run, i) => (
        <div key={run.id || i} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, borderTop: '1px solid #F1F1F1', paddingTop: 8 }}>
          <div>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>
              {jobLabel(run)}{run.month_key ? ` · ${run.month_key}` : ''} · {run.trigger === 'cron' ? 'Auto' : 'Manual'}
            </p>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#666' }}>{formatEuropeanDateTime(run.started_at)}</p>
            {runSummaryText(run) ? (
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#888' }}>{runSummaryText(run)}</p>
            ) : null}
            {run.status === 'failed' && run.error_message ? (
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#B42318' }}>{run.error_message}</p>
            ) : null}
          </div>
          <Badge
            text={run.status || 'unknown'}
            color={run.status === 'completed' ? 'green' : run.status === 'running' ? 'blue' : 'red'}
          />
        </div>
      ))}
    </Card>
  );
};

export const AdminFinancePanel = ({ financeOverview, loading, headers, selectedMonth, setSelectedMonth, onExportCsv, onExportUploadCsv, onGenerateBatch, onMarkHostPaid, onRunSchedulerNow, onRunConnectReminders }) => {
  const hosts = financeOverview?.hosts || [];
  const openBatch = financeOverview?.open_batch;
  const minimumPayout = financeOverview?.minimum_payout_eur || 8;
  const [hostFilter, setHostFilter] = useState('all');

  const filteredHosts = useMemo(() => {
    if (hostFilter === 'all') return hosts;
    if (hostFilter === 'ready') return hosts.filter((h) => !h.blocked && h.pending_balance > 0);
    if (hostFilter === 'blocked') return hosts.filter((h) => h.blocked);
    if (hostFilter === 'paid') return hosts.filter((h) => h.batch_item?.status === 'paid');
    return hosts;
  }, [hosts, hostFilter]);

  return (
    <div data-testid="admin-finance-panel" style={{ display: 'grid', gap: 16 }}>
      <PayoutScheduleCard
        financeOverview={financeOverview}
        onRunSchedulerNow={onRunSchedulerNow}
        onRunConnectReminders={onRunConnectReminders}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, flex: 1 }}>
          <Card style={summaryCardStyle}>
            <p style={{ margin: 0, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Pending host balance</p>
            <p data-testid="admin-finance-total-pending" style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>{formatMoney(financeOverview?.total_pending_balance)}</p>
          </Card>
          <Card style={summaryCardStyle}>
            <p style={{ margin: 0, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Monthly pool</p>
            <p data-testid="admin-finance-pool-total" style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>{formatMoney(financeOverview?.pool_total)}</p>
            <p style={{ margin: 0, fontSize: 12, color: '#666' }}>Qualified hosts: {financeOverview?.qualifying_host_count || 0}</p>
          </Card>
          <Card style={summaryCardStyle}>
            <p style={{ margin: 0, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Already paid</p>
            <p data-testid="admin-finance-total-paid" style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>{formatMoney(financeOverview?.total_paid_balance)}</p>
            <p style={{ margin: 0, fontSize: 12, color: '#666' }}>Generated hosts: {financeOverview?.generated_host_count || 0}</p>
          </Card>
        </div>

        <Card style={{ minWidth: 260, display: 'grid', gap: 12 }}>
          <label htmlFor="admin-finance-month-input" style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Payout month</span>
            <input
              id="admin-finance-month-input"
              type="month"
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(event.target.value)}
              data-testid="admin-finance-month-input"
              style={{ borderRadius: 12, border: '1px solid #E7E7E7', padding: '10px 12px', fontSize: 14 }}
            />
          </label>
          <button
            type="button"
            data-testid="admin-finance-generate-batch-button"
            onClick={onGenerateBatch}
            disabled={loading}
            style={{ border: 'none', borderRadius: 14, padding: '12px 16px', background: '#111', color: '#FFF', cursor: loading ? 'default' : 'pointer', fontWeight: 700 }}
          >
            {loading ? 'Updating…' : `Generate ${selectedMonth || 'month'} batch`}
          </button>
          <button
            type="button"
            data-testid="admin-finance-export-csv-button"
            onClick={onExportCsv}
            style={{ border: '1px solid #E7E7E7', borderRadius: 14, padding: '11px 16px', background: '#FFF', color: '#111', cursor: 'pointer', fontWeight: 700 }}
          >
            Export audit CSV
          </button>
          <button
            type="button"
            data-testid="admin-finance-export-upload-csv-button"
            onClick={onExportUploadCsv}
            style={{ border: 'none', borderRadius: 14, padding: '11px 16px', background: '#0F6B19', color: '#FFF', cursor: 'pointer', fontWeight: 700 }}
          >
            Export upload CSV
          </button>
        </Card>
      </div>

      <div data-testid="admin-finance-flow-diagram-card">
        <Card style={{ display: 'grid', gap: 14 }}>
          <div>
            <p style={{ margin: 0, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Allure money flow</p>
            <p style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 700 }}>Viva in → Allure ledger → payout rail out</p>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            {[
              'Guest pays in app',
              'Host earnings locked in Allure',
              'Admin generates monthly batch',
              'Upload CSV to your payout bank/provider',
              'Send to host IBANs',
            ].map((step, index, steps) => (
              <React.Fragment key={step}>
                <div style={{ borderRadius: 14, border: '1px solid #EFEFEF', padding: '12px 14px', background: '#FFF', fontSize: 14, fontWeight: 700 }}>{step}</div>
                {index < steps.length - 1 ? <ArrowRight size={18} color="#666" /> : null}
              </React.Fragment>
            ))}
          </div>
          <div style={{ borderTop: '1px solid #F1F1F1', paddingTop: 12, display: 'grid', gap: 4 }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Recommended setup</p>
            <p style={{ margin: 0, fontSize: 13, color: '#666', lineHeight: 1.5 }}>
              Keep <strong>Viva</strong> for incoming guest money and Allure’s internal ledger for host accounting. Use a separate payout-capable bank/provider later for outbound host transfers.
            </p>
            <p style={{ margin: 0, fontSize: 13, color: '#666', lineHeight: 1.5 }}>
              The new <strong>upload CSV</strong> is a generic bank-upload file: one row per host, with beneficiary name, IBAN, amount, and payment reference.
            </p>
            <p data-testid="admin-finance-upload-csv-note" style={{ margin: 0, fontSize: 13, color: '#666', lineHeight: 1.5 }}>
              Only hosts with saved IBAN details are included in the upload CSV.
            </p>
          </div>
        </Card>
      </div>

      <MoneyFlowBreakdownCard />

      <TierMixCard headers={headers} />

      {openBatch ? (
        <Card style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ margin: 0, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Open payout batch</p>
              <p data-testid="admin-finance-open-batch-title" style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 700 }}>
                {openBatch.month_key} · Batch {openBatch.sequence || 1}
              </p>
            </div>
            <Badge text={openBatch.status || 'generated'} color={openBatch.status === 'paid' ? 'green' : 'orange'} />
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#666' }}>
            Generated {formatEuropeanDateTime(openBatch.generated_at)} · Total {formatMoney(openBatch.total_amount)}
          </p>
          {(openBatch.blocked_hosts || []).length > 0 || (openBatch.below_minimum_hosts || []).length > 0 ? (
            <div data-testid="admin-finance-open-batch-rollover" style={{ display: 'grid', gap: 6, borderTop: '1px solid #F1F1F1', paddingTop: 10 }}>
              <p style={{ margin: 0, fontSize: 12, color: '#666', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Not included — rolls over to next cycle
              </p>
              {(openBatch.blocked_hosts || []).map((b) => (
                <div key={`blocked-${b.host_id}`} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                  <p style={{ margin: 0, fontSize: 13 }}>
                    <strong>{b.host_name}</strong> · {formatMoney(b.total_amount)}
                  </p>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {(b.block_reasons || []).map((reason) => <BlockReasonBadge key={reason} reason={reason} />)}
                  </div>
                </div>
              ))}
              {(openBatch.below_minimum_hosts || []).map((b) => (
                <div key={`below-min-${b.host_id}`} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                  <p style={{ margin: 0, fontSize: 13 }}>
                    <strong>{b.host_name}</strong> · {formatMoney(b.total_amount)}
                  </p>
                  <BlockReasonBadge reason="below_minimum" />
                </div>
              ))}
            </div>
          ) : null}
        </Card>
      ) : null}

      <div style={{ display: 'grid', gap: 12 }}>
        <div data-testid="admin-finance-host-filter" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: `All (${hosts.length})` },
            { key: 'ready', label: `Ready (${hosts.filter((h) => !h.blocked && h.pending_balance > 0).length})` },
            { key: 'blocked', label: `Blocked (${hosts.filter((h) => h.blocked).length})` },
            { key: 'paid', label: `Paid (${hosts.filter((h) => h.batch_item?.status === 'paid').length})` },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              data-testid={`admin-finance-filter-${tab.key}`}
              onClick={() => setHostFilter(tab.key)}
              style={{
                border: hostFilter === tab.key ? '2px solid #111' : '1px solid #E0E0E0',
                borderRadius: 12,
                padding: '8px 14px',
                background: hostFilter === tab.key ? '#111' : '#FFF',
                color: hostFilter === tab.key ? '#FFF' : '#111',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {filteredHosts.length === 0 ? <p style={{ textAlign: 'center', color: '#999', padding: 24 }}>No hosts match this filter.</p> : null}
        {filteredHosts.map((host) => {
          const batchItem = host.batch_item;
          const batchPaid = batchItem?.status === 'paid';
          const batchGenerated = batchItem?.status === 'generated';
          const isBlocked = host.blocked;
          return (
            <Card key={host.host_id} data-testid={`admin-finance-host-card-${host.host_id}`} style={{ display: 'grid', gap: 14, borderLeft: isBlocked ? '4px solid #F59E0B' : undefined }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 10 }}>
                <div>
                  <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{host.host_name}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>{host.host_email}</p>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Badge text={`${host.commission_rate}% rate`} color="blue" />
                  {host.qualifies_for_pool ? <Badge text="Pool qualified" color="green" /> : <Badge text="Pool not qualified" color="orange" />}
                  {(host.block_reasons || []).map((reason) => (
                    <BlockReasonBadge key={reason} reason={reason} />
                  ))}
                  {!host.blocked && host.pending_balance > 0 && host.pending_balance < minimumPayout ? (
                    <BlockReasonBadge reason="below_minimum" />
                  ) : null}
                </div>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {[
                  { icon: <Coins size={16} />, label: 'Pending', value: formatMoney(host.pending_balance) },
                  { icon: <CalendarDays size={16} />, label: 'Guests this month', value: host.month_guest_count },
                  { icon: <CheckCircle2 size={16} />, label: 'Estimated reward', value: formatMoney(host.estimated_pool_share) },
                  { icon: <Landmark size={16} />, label: 'Bank', value: host.bank_details?.iban_masked || 'Missing' },
                ].map((item) => (
                  <div key={`${host.host_id}-${item.label}`} style={{ minWidth: 140, flex: 1, borderRadius: 14, border: '1px solid #EFEFEF', padding: '10px 12px', display: 'grid', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#666', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      {item.icon}
                      <span>{item.label}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{item.value}</p>
                  </div>
                ))}
              </div>

              {isBlocked && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 12, background: '#FFF7EE', border: '1px solid #F3D9B0' }}>
                  <AlertTriangle size={16} color="#B45309" style={{ flexShrink: 0, marginTop: 2 }} />
                  <p style={{ margin: 0, fontSize: 12, color: '#7A4C0E', lineHeight: 1.5 }}>
                    <strong>Payout blocked:</strong>{' '}
                    {(host.block_reasons || []).map((r) => {
                      if (r === 'missing_vat') return 'Host has not provided their VAT/Tax ID.';
                      if (r === 'missing_iban') return 'Host has not connected a bank account.';
                      if (r === 'late_joiner') return `Host activated after the ${financeOverview?.late_joiner_cutoff_day || 25}th — eligible from ${host.payout_eligible_from || 'next cycle'}.`;
                      return r;
                    }).join(' ')}
                  </p>
                </div>
              )}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'grid', gap: 2 }}>
                  <p style={{ margin: 0, fontSize: 13, color: '#666' }}>
                    Commission {formatMoney(host.commission_pending)} · Host tips {formatMoney(host.host_tips_pending)} · Rewards {formatMoney(host.rewards_pending)}
                  </p>
                  {batchItem ? <p style={{ margin: 0, fontSize: 12, color: '#666' }}>Batch amount {formatMoney(batchItem.total_amount)} · Entries {batchItem.entries_count}</p> : null}
                  {host.payout_eligible_from && (
                    <p style={{ margin: 0, fontSize: 11, color: '#888' }}>
                      Eligible from: {host.payout_eligible_from}
                    </p>
                  )}
                </div>

                {openBatch && batchGenerated && !isBlocked ? (
                  <button
                    type="button"
                    data-testid={`admin-finance-mark-paid-${host.host_id}`}
                    onClick={() => onMarkHostPaid(openBatch.id, host.host_id)}
                    style={{ border: 'none', borderRadius: 14, padding: '10px 14px', background: '#00A246', color: '#FFF', cursor: 'pointer', fontWeight: 700 }}
                  >
                    Mark paid
                  </button>
                ) : batchPaid ? (
                  <Badge text="Paid" color="green" />
                ) : isBlocked && batchGenerated ? (
                  <Badge text="Blocked" color="orange" />
                ) : null}
              </div>
            </Card>
          );
        })}
      </div>

      <SchedulerRunsCard runs={financeOverview?.scheduler_runs} />

      <Card style={{ display: 'grid', gap: 10 }}>
        <p style={{ margin: 0, fontSize: 12, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Recent payout batches</p>
        {(financeOverview?.recent_batches || []).length === 0 ? <p style={{ margin: 0, color: '#999' }}>No batches yet</p> : financeOverview.recent_batches.map((batch) => (
          <div key={batch.id} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 10, borderTop: '1px solid #F1F1F1', paddingTop: 10 }}>
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{batch.month_key} · Batch {batch.sequence}</p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#666' }}>{formatEuropeanDateTime(batch.generated_at)}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{formatMoney(batch.total_amount)}</p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#666' }}>{batch.host_count} host{batch.host_count === 1 ? '' : 's'} · {batch.status}</p>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
};
